// ─── Owl/Spark chat: following the page's scope ───────────────────────────────
// One rule, in one place: WHAT the chat should be scoped to when the page moves
// under it, and whether that move ENDS the conversation.
//
// The chat's own header pickers have always started a fresh conversation on a
// scope change ("don't mix clients' data in a thread"). The same change made from
// OUTSIDE the chat — the profile switcher, or navigating off an event — did not:
// the thread carried on with the previous client/event still in its history while
// the server rebuilt the system prompt for the NEW scope. Asked why it had used
// BCW2027, Spark answered from that history and called it "the event currently
// open in your Pulse session" — after the person had switched to another profile
// (2026-08-21). The server forks the thread on the same mismatch (see
// threadScopeMatches in server/owlChat.js); this is the UI half.
//
// Pure so it can be reasoned about (and tested) without a browser.

const norm = (s) => ({ entityId: (s && s.entityId) || '', suiteId: (s && s.suiteId) || '' });

// from/to  = the page's scope before and after the change
// selected = what the chat is scoped to right now (may be a manual header pick)
// live     = the conversation has messages, i.e. there is history to protect
// → { entityId, suiteId, reset } to apply, or null to leave the chat alone.
export function followPageScope({ from, to, selected, live = false } = {}) {
  const f = norm(from); const t = norm(to); const sel = norm(selected);
  if (f.entityId === t.entityId && f.suiteId === t.suiteId) return null; // page didn't move
  // Another CLIENT: follow it, drop the old event, and never carry one client's
  // conversation into another's answers.
  if (t.entityId !== f.entityId) return { entityId: t.entityId, suiteId: t.suiteId, reset: !!live };
  // Another EVENT on the same client: follow it whole (event and its client, which
  // belong together), and start fresh so the history isn't about a different
  // edition than the prompt says.
  if (t.suiteId) return { entityId: t.entityId, suiteId: t.suiteId, reset: !!live };
  // The page dropped its event (navigated off it). Widening a LIVE chat to "all
  // events" is exactly what made the answer lie, and wiping it would cost the
  // person a conversation they didn't ask to end — so stay pinned to the event it
  // is about (the header picker still shows it, and still changes it). With
  // nothing said yet there's no history to protect: just follow the page.
  return live ? null : { entityId: sel.entityId, suiteId: '', reset: false };
}
